import {
  AudienceIcon,
  Card,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  NoAudienceIcon,
  PlusIcon,
  SwitchColorEnum,
  SwitchInside,
  Typography,
  UnLinkIcon,
  useWindowWidth,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Clock } from '@/components/Clock';
import { Divider } from '@/components/Divider/Divider';
import { EmptyStateErrorUnexpected } from '@/components/EmptyState';
import { RelaunchEnrichmentsModal } from '@/components/RelaunchEnrichmentsModal/RelaunchEnrichmentsModal';
import { MOBILE_WIDTH } from '@/constants';
import { AudiencesTable } from '@/features/audiencias/components/AudiencesTable/AudiencesTable';
import { useAudiencePermissions } from '@/features/audiencias/hooks/useAudiencePermissions';
import { TAudience } from '@/features/audiencias/types';
import { useChannelId } from '@/features/broadcasters/api/get-channel-id';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { FormTypeEnum, TableSelection } from '@/types';

import { useGetBroadcastSchedule } from '../api/get-broadcast-schedule';
import { BroadcastScheduleHeaderFilters } from '../components/BroadcastScheduleHeaderFilters/BroadcastScheduleHeaderFilters';
import { BroadcastScheduleTable } from '../components/BroadcastScheduleTable';
import { ProgramBroadcastDrawerForm } from '../components/ProgramBroadcastDrawerForm/ProgramBroadcastDrawerForm';
import { useBroadcastSchedulePermissions } from '../hooks/useBroadcastSchedulePermissions';
import { useProgramBroadcastController } from '../hooks/useProgramBroadcastController';
import { useRelaunchBroadcastController } from '../hooks/useRelauchBroadcastController';
import { TProgramBroadcast } from '../types';

export default function BroadcastSchedulePage() {
  const { t } = useTranslation();
  const { width } = useWindowWidth();
  const [isAudiencesMode, setIsAudiencesMode] = useState(false);

  const { data: programsData, isLoading, isError } = useGetBroadcastSchedule();

  const isMobile = width < MOBILE_WIDTH;

  const {
    programBroadcastDrawer,
    handleSubmitProgramBroadcast,
    isMutatingProgramBroadcast,
    audiencesUsed,
    unlinkAudienceFileDrawer,
    handleUnlinkAudienceFile,
    programBroadcastDialog,
    handleDeleteProgramBroadcast,
  } = useProgramBroadcastController();
  const { handleRelaunchBroadcast, relaunchBroadcastDrawer } = useRelaunchBroadcastController();
  const { data: channelData } = useChannelId({
    id: relaunchBroadcastDrawer.data?.channel_id || '',
  });

  const { canSee: canSeeAudienceData } = useAudiencePermissions();
  const { canCreate, canUpdate, canDelete } = useBroadcastSchedulePermissions();

  const onCreate = () => {
    if (!canCreate) return;
    programBroadcastDrawer.open({ type: FormTypeEnum.CREATE, data: null });
  };

  const onUpdate = (programBroadcast: TProgramBroadcast) => {
    if (!canUpdate) return;
    programBroadcastDrawer.open({
      type: FormTypeEnum.UPDATE,
      data: {
        audiences: [],
        programBrocast: programBroadcast,
      },
    });
  };

  const onDelete = (programBroadcast: TProgramBroadcast) => {
    if (!canDelete) return;
    programBroadcastDialog.open(programBroadcast);
  };

  const onAddScheduleEmision = (selection: TableSelection<TAudience>) => {
    if (!canCreate) return;
    programBroadcastDrawer.open({
      type: FormTypeEnum.CREATE,
      data: {
        audiences: selection.selectedItems,
      },
    });
  };

  return (
    <>
      <div className="flex flex-col gap-5 pb-10">
        <div className="flex items-center">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Parrilla de emisión')}
          </Typography>

          <div className="px-[100px]">
            <BroadcastScheduleHeaderFilters isAudiencesMode={isAudiencesMode} />
          </div>

          <div className="ml-auto flex items-center gap-5">
            {canSeeAudienceData && (
              <SwitchInside
                checked={isAudiencesMode}
                color={SwitchColorEnum.SUCCESS}
                label={t('Audiencias')}
                onChange={setIsAudiencesMode}
                icon={
                  isAudiencesMode ? (
                    <AudienceIcon className={'!size-[12px] text-feedback-success-30'} />
                  ) : (
                    <NoAudienceIcon className={'!size-[12px] text-text-medium-10'} />
                  )
                }
              />
            )}

            {canCreate && (
              <IconButtonSection
                onClick={onCreate}
                internalProps={{
                  icon: {
                    size: IconSizeEnum.SM,
                    type: PlusIcon,
                  },
                  text: {
                    value: t('Nuevo'),
                    color: FontColorEnum.Default,
                  },
                }}
              />
            )}

            <Clock />
          </div>
        </div>

        {isError ? (
          <EmptyStateErrorUnexpected />
        ) : (
          <div className="flex gap-2">
            {isAudiencesMode && (
              <div className="w-full">
                <Card>
                  <Typography
                    fontSize={FontSizeEnum.Xl}
                    weight={FontWeightEnum.Bold}
                    color={FontColorEnum.Dark10}
                  >
                    {t('Archivo Audiencias')}
                  </Typography>

                  <Divider />

                  <AudiencesTable
                    onAddScheduleEmision={canCreate ? onAddScheduleEmision : undefined}
                  />
                </Card>
              </div>
            )}

            <div className="main flex min-h-full w-full flex-wrap gap-size-24">
              <div className={clsx('column-1 flex h-auto w-full flex-col items-start gap-size-8')}>
                <Card
                  classes={{
                    header: clsx(!isMobile && 'py-3'),
                  }}
                >
                  <div className="min-h-[400px]">
                    <BroadcastScheduleTable
                      isAudiencesMode={isAudiencesMode}
                      items={programsData?.data || []}
                      totalItems={programsData?.total || 0}
                      onUpdate={onUpdate}
                      onSegments={item => {
                        const params = new URLSearchParams();
                        params.append('gte', item.start_datetime);
                        params.append('lte', item.end_datetime);

                        window.open(
                          `./broadcast_schedule/${item.id}?${params.toString()}`,
                          '_blank',
                          'noopener,noreferrer'
                        );
                      }}
                      onAiRedo={item => {
                        relaunchBroadcastDrawer.open(item);
                      }}
                      isFetching={isLoading}
                      onUnlink={item => unlinkAudienceFileDrawer.open(item)}
                      onDelete={onDelete}
                    />
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>

      <ProgramBroadcastDrawerForm
        isOpen={programBroadcastDrawer.isOpen}
        data={programBroadcastDrawer.data}
        onClose={programBroadcastDrawer.close}
        onSubmit={handleSubmitProgramBroadcast}
        isMutating={isMutatingProgramBroadcast}
        audiencesUsed={audiencesUsed}
      />

      <ModalDialog
        isOpen={unlinkAudienceFileDrawer.isOpen}
        onClose={unlinkAudienceFileDrawer.close}
        onAction={handleUnlinkAudienceFile}
        heading={t('Desvincular archivo de audiencias')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.WARNING}
      >
        <ModalMessage
          title={t('¿Estás seguro de querer desvincular el archivo de audiencias?')}
          icon={UnLinkIcon}
        />
      </ModalDialog>

      <ModalDialog
        isOpen={programBroadcastDialog.isOpen}
        onClose={programBroadcastDialog.close}
        onAction={handleDeleteProgramBroadcast}
        heading={t('Eliminar emisión')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage title={t('¿Estás seguro de querer eliminar la emisión seleccionada?')} />
      </ModalDialog>

      <RelaunchEnrichmentsModal
        isOpen={relaunchBroadcastDrawer.isOpen}
        onClose={relaunchBroadcastDrawer.close}
        onAction={enrichments =>
          handleRelaunchBroadcast(relaunchBroadcastDrawer.data?.id || '', enrichments)
        }
        heading={t('Relanzar procesos de enriquecimiento')}
        actionLabel={t('Lanzar procesos')}
        allowAdvertising
        isRadio={channelData?.media_type === ChannelMediaTypeEnum.RADIO}
      />
    </>
  );
}
